import { GetChatsParams, ChatType } from "@/orval/gen/model"
import { getChats, getGetChatsQueryKey, useJoinPublicGroup } from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { CancelButton, SubmitButton } from "@components/Buttons"
import { DefaultModal } from "@components/default-modal"
import { FormButtonGroup } from "@components/form-button-group"
import { InfiniteSelect } from "@components/InfiniteSelect"
import { ModalProps, SelectProps, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"

interface FormValues {
  chatId: number
}

export function JoinPublicGroupModal(props: ModalProps) {
  const form = useForm<FormValues>({ mode: "uncontrolled" })
  const navigate = useNavigate()
  const { mutateAsync: joinGroup } = useJoinPublicGroup({
    mutation: {
      onSuccess: (_, req) => {
        navigate({
          to: "/app/com/chat/$chatId",
          params: { chatId: req.params.chatId.toString() },
        })
        props.onClose()
      },
      onError: showErrorNotification,
    },
  })
  const handleSubmit = async (values: FormValues) =>
    await joinGroup({ params: values })

  const { t } = useTranslation("app_server")
  return (
    <DefaultModal title={t("JoinGroup")} {...props}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <ChatGroupSelect
            label={t("Groups")}
            key={form.key("chatId")}
            {...form.getInputProps("chatId")}
          />
          <FormButtonGroup>
            <CancelButton onClick={props.onClose} />
            <SubmitButton disabled={!form.getValues().chatId}>
              {t("JoinGroup")}
            </SubmitButton>
          </FormButtonGroup>
        </Stack>
      </form>
    </DefaultModal>
  )
}

function ChatGroupSelect(props: Omit<SelectProps, "data" | "scrollAreaProps">) {
  const request: Omit<GetChatsParams, "pageParam" | "pageSize"> = {
    chatTypeFilters: [ChatType.PublicGroup],
    userMemberFilter: "UserNotIsMember",
    withUnreadCount: false,
  }
  return (
    <InfiniteSelect
      queryResult={useDefaultInfiniteQuery(
        getChats,
        (pageRequest) => {
          return {
            ...pageRequest,
            ...request,
          }
        },
        { pageSize: 25 }
      )}
      mapDataToOption={(data) =>
        data?.pages
          .flatMap((p) => p.data)
          .map((c) => ({ label: c.name, value: c.chatId.toString() }))
      }
      {...props}
    />
  )
}
